'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/context/auth-context';
import { useWishlist } from '@/context/wishlist-context';
import { formatPrice } from '@/lib/shopify';
import AccountLayout from '@/components/account/account-layout';

export default function AccountDashboardClient() {
  const { customer, isLoading, isAuthenticated } = useAuth();
  const { itemCount: wishlistCount } = useWishlist();
  const router = useRouter();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      router.push('/account/login');
    }
  }, [isLoading, isAuthenticated, router]);

  if (isLoading) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#000000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !customer) {
    return null;
  }

  const recentOrders = customer.orders.slice(0, 3);

  const statCardStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
  };

  return (
    <AccountLayout>
      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '32px',
      }}>
        <div style={statCardStyle}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#f3f4f6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#374151" style={{ width: '24px', height: '24px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
            </svg>
          </div>
          <p style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>{customer.orders.length}</p>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Orders</p>
        </div>

        <div style={statCardStyle}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="#ef4444" viewBox="0 0 24 24" style={{ width: '24px', height: '24px' }}>
              <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          </div>
          <p style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>{wishlistCount}</p>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Wishlist</p>
        </div>

        <div style={statCardStyle}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#f0fdf4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#16a34a" style={{ width: '24px', height: '24px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
          </div>
          <p style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>{customer.addresses.length}</p>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Addresses</p>
        </div>
      </div>

      {/* Recent Orders */}
      <div style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '24px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Recent Orders
          </h2>
          {customer.orders.length > 0 && (
            <Link href="/account/orders" style={{ fontSize: '13px', color: '#000000', textDecoration: 'underline' }}>
              View All
            </Link>
          )}
        </div>

        {recentOrders.length > 0 ? (
          <div>
            {recentOrders.map((order, index) => (
              <div
                key={order.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  borderBottom: index < recentOrders.length - 1 ? '1px solid #e5e7eb' : 'none',
                }}
              >
                <div>
                  <p style={{ fontWeight: '600', marginBottom: '4px' }}>{order.name}</p>
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>
                    {new Date(order.processedAt).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                    {' · '}
                    {order.lineItems.length} {order.lineItems.length === 1 ? 'item' : 'items'}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: '600', marginBottom: '4px' }}>{formatPrice(order.currentTotalPrice)}</p>
                    <p style={{
                      fontSize: '12px',
                      color: order.fulfillmentStatus === 'FULFILLED' ? '#16a34a' : '#f59e0b',
                      fontWeight: '500',
                    }}>
                      {order.fulfillmentStatus === 'FULFILLED' ? 'Delivered' :
                       order.fulfillmentStatus === 'IN_PROGRESS' ? 'In Transit' :
                       order.fulfillmentStatus === 'UNFULFILLED' ? 'Processing' :
                       order.fulfillmentStatus.replace('_', ' ')}
                    </p>
                  </div>
                  <Link
                    href={order.statusUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px 16px',
                      border: '1px solid var(--brand-blue)',
                      fontSize: '12px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      color: 'var(--brand-blue)',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Track
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="#d1d5db" style={{ width: '64px', height: '64px', margin: '0 auto 16px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
            </svg>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>No orders yet</p>
            <Link
              href="/collections"
              style={{
                display: 'inline-block',
                padding: '12px 32px',
                backgroundColor: 'var(--brand-blue)',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                borderRadius: '6px',
              }}
            >
              Start Shopping
            </Link>
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
      }}>
        {/* Default Address Card */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Default Address
            </h3>
            <Link href="/account/addresses" style={{ fontSize: '12px', color: '#000000', textDecoration: 'underline' }}>
              Edit
            </Link>
          </div>
          {customer.defaultAddress ? (
            <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
              <p style={{ fontWeight: '500', color: '#000000' }}>
                {customer.defaultAddress.firstName} {customer.defaultAddress.lastName}
              </p>
              <p>{customer.defaultAddress.address1}</p>
              {customer.defaultAddress.address2 && <p>{customer.defaultAddress.address2}</p>}
              <p>{customer.defaultAddress.city}, {customer.defaultAddress.zip}</p>
              <p>{customer.defaultAddress.country}</p>
            </div>
          ) : (
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>No default address set</p>
          )}
        </div>

        {/* Account Info Card */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Account Info
            </h3>
            <Link href="/account/personal-info" style={{ fontSize: '12px', color: '#000000', textDecoration: 'underline' }}>
              Edit
            </Link>
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
            <p style={{ fontWeight: '500', color: '#000000' }}>
              {customer.firstName} {customer.lastName}
            </p>
            <p>{customer.email}</p>
            {customer.phone && <p>{customer.phone}</p>}
            <p style={{ marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
              Member since {new Date(customer.createdAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}
